import React, { useState } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Clock,
  Loader2,
  FileText,
  CalendarDays,
  CheckCircle2
} from 'lucide-react';
import moment from 'moment';
import axios from 'axios';
import { usePostModal } from '../../contexts/PostModalContext';
import { useQuery } from '@tanstack/react-query';
import { cn } from "../../lib/utils";
import { getPostText } from '../../utils/general';

// shadcn UI
import { Button } from "../ui/button";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "../ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { Separator } from "../ui/separator";
import { ScrollArea } from "../ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { Badge } from "../ui/badge";

import { API_BASE_URL } from '../../config';

interface Post {
  id: string;
  createdAt: string;
  textEn?: string;
  textEs?: string;
  translations?: Record<string, { text: string, tiktokTitle?: string }>;
  config?: any;
  game?: {
    id: string;
    name: string;
  };
  media: Array<{
    id: string;
    filePath: string;
    thumbnailPath?: string;
    mediaType: string;
  }>;
  schedules: Array<{
    id: string;
    socialAccountId: string;
    scheduledAt: string;
    status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  }>;
}

export default function ScheduleCalendar(): JSX.Element {
  const [currentDate, setCurrentDate] = useState(moment());
  const { openPostModal, openPostById } = usePostModal();
  const token = localStorage.getItem('token');

  // Use React Query for fetching posts
  const { data: posts = [], isLoading: loading } = useQuery<Post[]>({
    queryKey: ['posts'],
    queryFn: async () => {
      if (!token) return [];
      const response = await axios.get(`${API_BASE_URL}/posts/user`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Handle both old array format and new object format { posts: [], updatedStats: boolean }
      if (Array.isArray(response.data)) {
        return response.data;
      }
      return response.data.posts || [];
    },
    enabled: !!token,
  });

  // Popover state
  const [openSubmenu, setOpenSubmenu] = useState(false);
  const [selectedDayPosts, setSelectedDayPosts] = useState<Post[]>([]);
  const [selectedDayLabel, setSelectedDayLabel] = useState("");

  const handleDayClick = (day: number | null) => {
    if (!day) return;
    
    const dateStr = currentDate.clone().date(day).format('YYYY-MM-DD');
    const postsForDay = posts
      .filter(post => 
        post.schedules.some(s => moment(s.scheduledAt).format('YYYY-MM-DD') === dateStr)
      )
      .sort((a, b) => {
        const timeA = Math.min(...a.schedules.map(s => moment(s.scheduledAt).valueOf()));
        const timeB = Math.min(...b.schedules.map(s => moment(s.scheduledAt).valueOf()));
        return timeA - timeB;
      });

    if (postsForDay.length === 1) {
      handleOpenPost(postsForDay[0]);
    } else if (postsForDay.length > 1) {
      setSelectedDayPosts(postsForDay);
      setSelectedDayLabel(currentDate.clone().date(day).format('MMM D'));
      setOpenSubmenu(true);
    }
  };

  const handleOpenPost = (post: Post) => {
    openPostById(post.id);
    setOpenSubmenu(false);
  };

  const daysInMonth = currentDate.daysInMonth();
  const startDay = currentDate.clone().startOf('month').day();
  const monthName = currentDate.format('MMMM');
  const year = currentDate.format('YYYY');

  const days = [];
  const adjustedStartDay = startDay === 0 ? 6 : startDay - 1;

  for (let i = 0; i < adjustedStartDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  const weekDays = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  const prevMonth = () => setCurrentDate(currentDate.clone().subtract(1, 'month'));
  const nextMonth = () => setCurrentDate(currentDate.clone().add(1, 'month'));

  const hasCompletedPost = (day: number | null) => {
    if (!day) return false;
    const dateStr = currentDate.clone().date(day).format('YYYY-MM-DD');
    return posts.some(post => 
      post.schedules.some(s => 
        s.status === 'COMPLETED' && moment(s.scheduledAt).format('YYYY-MM-DD') === dateStr
      )
    );
  };

  return (
    <Card className="bg-card border-border shadow-xl overflow-hidden h-fit">
      <CardHeader className="border-b border-border/50 bg-gradient-to-r from-primary/10 via-transparent to-transparent">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-black text-foreground uppercase tracking-[0.2em] flex items-center gap-2">
            <div className="p-1.5 bg-primary/20 rounded-lg shadow-[0_0_15px_hsl(var(--primary)/0.2)]">
              <CalendarDays className="h-4 w-4 text-primary" />
            </div>
            Calendar
            {loading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground ml-1" />}
          </CardTitle>
          <div className="flex items-center bg-background rounded-lg border border-border p-0.5">
            <Button variant="ghost" size="icon" onClick={prevMonth} className="h-7 w-7 text-muted-foreground hover:text-foreground">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-[11px] font-black text-foreground uppercase tracking-widest min-w-[100px] text-center">
              {monthName} {year}
            </span>
            <Button variant="ghost" size="icon" onClick={nextMonth} className="h-7 w-7 text-muted-foreground hover:text-foreground">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-4 pt-0">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map((day, i) => (
            <div key={i} className="text-center">
              <span className="text-[10px] font-black text-muted-foreground uppercase tracking-tighter">{day}</span>
            </div>
          ))}
        </div>

        <Separator className="bg-card mb-3" />

        <div className="grid grid-cols-7 gap-1">
          {days.map((day, index) => {
            const isToday = day === moment().date() && currentDate.isSame(moment(), 'month');
            const hasPost = hasCompletedPost(day);
            
            return (
              <div key={index} className="aspect-square relative">
                {day && (
                  <Popover open={openSubmenu && selectedDayLabel === currentDate.clone().date(day).format('MMM D')} onOpenChange={(o) => !o && setOpenSubmenu(false)}>
                    <PopoverTrigger asChild>
                      <button
                        onClick={() => handleDayClick(day)}
                        className={cn(
                          "w-full h-full flex flex-col items-center justify-center rounded-lg transition-all duration-200 outline-none border",
                          day ? "cursor-pointer hover:bg-card/50" : "cursor-default",
                          isToday ? "bg-primary/10 border-primary/50 text-primary" : "bg-transparent border-transparent text-muted-foreground",
                          !isToday && day && "hover:border-border"
                        )}
                      >
                        <span className={cn("text-xs", isToday ? "font-black" : "font-medium")}>
                          {day}
                        </span>
                        {hasPost && (
                          <div className="absolute bottom-1.5 h-1 w-1 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]" />
                        )}
                      </button>
                    </PopoverTrigger>
                    
                    <PopoverContent className="w-80 bg-card border-border p-0 shadow-2xl overflow-hidden" side="right" align="start">
                      <div className="bg-background px-4 py-3 border-b border-border">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-black text-foreground uppercase tracking-widest flex items-center gap-2">
                            <FileText className="h-4 w-4 text-primary" />
                            Posts • {selectedDayLabel}
                          </h4>
                          <Badge className="bg-primary text-[10px] font-black">{selectedDayPosts.length}</Badge>
                        </div>
                      </div>
                      <ScrollArea className="max-h-[300px]">
                        <div className="p-1">
                          {selectedDayPosts.map((post) => {
                            const displayText = getPostText(post);
                            const scheduledAt = moment(post.schedules[0]?.scheduledAt);
                            const time = scheduledAt.format('HH:mm');
                            const isFuture = scheduledAt.isAfter(moment());

                            return (
                              <button
                                key={post.id}
                                onClick={() => handleOpenPost(post)}
                                className="w-full text-left p-3 rounded-lg hover:bg-primary/10 group transition-all flex items-center gap-3 border border-transparent hover:border-primary/20"
                              >
                                <div className="flex flex-col items-center shrink-0">
                                  {isFuture ? (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <div className="bg-amber-500/10 p-1.5 rounded-lg border border-amber-500/20">
                                            <Clock className="h-3.5 w-3.5 text-amber-500" />
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent className="bg-muted border-border text-[10px]">Scheduled</TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  ) : (
                                    <div className="bg-primary/10 p-1.5 rounded-lg border border-primary/20">
                                      <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                                    </div>
                                  )}
                                  <span className={cn(
                                    "text-[10px] font-black mt-1",
                                    isFuture ? "text-amber-500" : "text-primary"
                                  )}>{time}</span>
                                </div>
                                <div className="h-8 w-px bg-card" />
                                <div className="flex-1 overflow-hidden">
                                  <p className="text-xs text-foreground font-medium line-clamp-2 leading-relaxed group-hover:text-foreground">
                                    {displayText}
                                  </p>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </ScrollArea>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
